'use client'

/**
 * MyCIO / WealthOS auth client — DELIBERATELY SEPARATE from a retired portfolio session.
 *
 * It hits the SAME Supabase project + anon key (required: RLS on aicio_scenarios /
 * plan_comments / etc. keys to this project's auth.uid(); a second project would break every
 * plan query), but it uses its OWN GoTrue storageKey ('mycio-auth') so a MyCIO login is a
 * distinct session from a retired portfolio login. They never read/write each other's tokens.
 *
 * Do NOT import the legacy `@/lib/supabase` client into the MyCIO app, and do not import
 * this client into the trading app.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { SUPABASE_PUBLIC_URL, SUPABASE_PUBLIC_ANON_KEY } from '@/lib/supabase-config'

// CROSS-BUNDLE SINGLETON (the 20s / "10s to open the plan" fix). A plain module-scoped `let client`
// is NOT a real singleton under Turbopack: the same module can be emitted into more than one chunk, so
// each copy gets its OWN `client` var and constructs its OWN GoTrue client -- both under storageKey
// 'mycio-auth', both with autoRefreshToken. Two clients on one rotating refresh token then revoke each
// other on every tick -> token_revoked -> retry -> `429 retry-after:10` -> the page blocks ~10s. That is
// exactly what production showed ("Multiple GoTrueClient instances detected ... same storage key" + a
// _callRefreshToken storm). Pinning the instance (and the single-flight state) on globalThis makes every
// duplicated module copy return the SAME client, so there is one session, one auto-refresh, one lock.
const CLIENT_KEY = Symbol.for('mycio.supabase.client')
const SESSION_INFLIGHT_KEY = Symbol.for('mycio.supabase.sessionInflight')
const REFRESH_INFLIGHT_KEY = Symbol.for('mycio.supabase.refreshInflight')
type MyCIOGlobal = typeof globalThis & {
  [CLIENT_KEY]?: SupabaseClient
  [SESSION_INFLIGHT_KEY]?: Promise<string | null> | null
  [REFRESH_INFLIGHT_KEY]?: Promise<string | null> | null
}
const g = globalThis as MyCIOGlobal

/** The browser-side MyCIO auth client (process-wide singleton, duplication-proof). Persists into its own storage key. */
export function getMyCIOClient(): SupabaseClient {
  const existing = g[CLIENT_KEY]
  if (existing) return existing
  // Diagnostic: this must run exactly ONCE per tab. If it logs more than once, a bundle copy bypassed the
  // globalThis pin (should not happen) -- previously that meant two clients autoRefreshing -> token_revoked storm.
  if (typeof console !== 'undefined') console.info('[mycio-auth] Supabase client constructed')
  const created = createClient(SUPABASE_PUBLIC_URL, SUPABASE_PUBLIC_ANON_KEY, {
    auth: {
      // The separation lever: a unique storage key so MyCIO and legacy sessions never collide.
      storageKey: 'mycio-auth',
      persistSession: true,
      // autoRefreshToken is ON, and that is CORRECT once there is exactly ONE client (the globalThis pin).
      // Empirically verified in the live browser: with one client, N concurrent getSession()/auto-refresh
      // callers on an expired token collapse to EXACTLY ONE /token call (the SDK's internal lock + check-then-
      // refresh dedupe them). The earlier storm was NOT the SDK auto-refresh -- it was FORCED refreshSession()
      // calls racing (a forced refresh does not dedupe; each rotates the token -> stale-token retries -> 429).
      // So the fix is: one client + let the SDK own refresh (deduped) + NEVER force on the hot path (see
      // resolveMyCIOToken below; forceRefreshMyCIOToken is reserved for a real 401).
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    realtime: { params: { eventsPerSecond: 0 } },
  })
  g[CLIENT_KEY] = created
  return created
}

// --- shared, SINGLE-FLIGHT token resolution ---------------------------------------------------------
// After login the plan page + dashboard + their child fetches all need a valid access token at the same
// instant. If each independently calls refreshSession(), Supabase's ROTATING refresh token means the
// first refresh revokes the token the others hold -> every sibling gets token_revoked and retries -> a
// self-feeding "refresh storm" (observed: 7 refreshes in 21s, ~30s to reach the plan page). These two
// helpers collapse all concurrent callers onto ONE in-flight promise so at most one refresh ever runs.
// NOTE: these live on globalThis (see keys above), not module scope, so a duplicated bundle copy shares
// the same in-flight promise -- otherwise each copy has its own guard and the "single flight" isn't single.

/**
 * Resolve the current access token WITHOUT forcing a refresh: reads the stored session (getSession is
 * lock-guarded internally) and only refreshes when the token is actually at/near expiry — that refresh
 * shares the single-flight below. Concurrent callers get the same promise. Use for normal data loads.
 */
export function resolveMyCIOToken(): Promise<string | null> {
  const inflight = g[SESSION_INFLIGHT_KEY]
  if (inflight) return inflight
  const p = (async () => {
    // getSession() returns the stored token and, when it's expired, refreshes it — and CRUCIALLY it dedupes:
    // many concurrent getSession() callers on one client resolve to a SINGLE /token refresh (verified live:
    // 6 concurrent -> 1 POST, all six get the same new token). We do NOT force a refresh here (a forced
    // refreshSession() does not dedupe -> the storm). The app-level single-flight below is belt-and-suspenders
    // on top of the SDK's own dedup.
    const { data: { session } } = await getMyCIOClient().auth.getSession()
    return session?.access_token ?? null
  })().finally(() => { g[SESSION_INFLIGHT_KEY] = null })
  g[SESSION_INFLIGHT_KEY] = p
  return p
}

/**
 * Force ONE refresh, shared across concurrent callers. Use ONLY after a real 401 (token revoked/expired
 * server-side). Because it is single-flight, seven components hitting a 401 at once trigger exactly one
 * refreshSession() — the rotating refresh token is never revoked out from under a sibling call.
 */
export function forceRefreshMyCIOToken(): Promise<string | null> {
  const inflight = g[REFRESH_INFLIGHT_KEY]
  if (inflight) return inflight
  const p = (async () => {
    const sb = getMyCIOClient()
    const { data } = await sb.auth.refreshSession()
    const token = data.session?.access_token ?? null
    if (token) return token
    // refreshSession() returning no token usually means a CONCURRENT refresh already WON and rotated the
    // token (this call then sees token_revoked) — the session is NOT dead. Re-read it and use the winner's
    // token if it's still valid. Only a genuinely absent/expired session returns null (caller signs in).
    // We must NEVER signOut here: nuking a live session on a lost race caused a re-auth thrash (~20s) and
    // "cannot save".
    const { data: { session } } = await sb.auth.getSession()
    const expMs = session?.expires_at ? session.expires_at * 1000 : 0
    if (session?.access_token && (!expMs || expMs - Date.now() > 0)) return session.access_token
    return null
  })().finally(() => { g[REFRESH_INFLIGHT_KEY] = null })
  g[REFRESH_INFLIGHT_KEY] = p
  return p
}

let recoveryRequestClient: SupabaseClient | null = null

/**
 * Non-PKCE client used ONLY to REQUEST emails that contain a link: password reset, magic link, and the sign-in code
 * email (whose button is also a link). A PKCE link can only be completed in the browser that requested it (the code
 * verifier lives in that browser's storage), and these emails are usually opened from a mail app in a different
 * browser. Requested without PKCE, the email carries a token_hash that /auth/callback verifies in any browser. Typed
 * codes still verify on the main client. This client never stores a session.
 */
export function getMyCIOEmailLinkRequestClient(): SupabaseClient {
  if (!recoveryRequestClient) {
    recoveryRequestClient = createClient(SUPABASE_PUBLIC_URL, SUPABASE_PUBLIC_ANON_KEY, {
      auth: {
        storageKey: 'mycio-auth-recovery-request',
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        flowType: 'implicit',
      },
    })
  }
  return recoveryRequestClient
}
